import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  DisplayUser,
  GalleryDto,
  GalleryPublicAccessLevel,
  GalleryTenantAccessLevel,
  TenantDto,
} from '@nusign/picflow-backend-types';
import { Observable, firstValueFrom, lastValueFrom, map, of, switchMap, take } from 'rxjs';
import { billingScopes } from '../billing-consts';
import { accessLevelOrder } from '../consts';
import { GalleryAccessLevel } from '../lib/access-level';
import { forkJoinSafe } from '../lib/safe-fork-join';
import { IGNORE_AUTH_ERRORS } from '../network-interceptor';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { GalleryPasswordService } from './gallery-password.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { PermissionService } from './permission.service';
import { StateService } from './state.service';
import { TenantService } from './tenant.service';
import { UserGroupService } from './user-group.service';

@Injectable({
  providedIn: 'root',
})
export class GalleryAccessLevelSubService {
  private state = inject(StateService);
  private tenantService = inject(TenantService);
  private galleryPasswordService = inject(GalleryPasswordService);
  private loader = inject(DataLoaderService);
  private observableLoader = inject(ObservableDataLoaderService);
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private userGroupService = inject(UserGroupService);
  private permissionService = inject(PermissionService);

  public async getAccessLevel(
    gallery: GalleryDto,
    sharedWithMeGalleries: (GalleryDto & { access_level: GalleryAccessLevel })[],
    options: { ignorePublicAccessLevel?: boolean } = {}
  ): Promise<GalleryAccessLevel> {
    const galleryTenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
    const [tenantAccessLevel, _publicAccessLevel] = await this.getAccessLevels(gallery);
    if (!this.state.user) return _publicAccessLevel || 'no_access'; // non-users always get the public access level

    const publicAccessLevel = options.ignorePublicAccessLevel ? 'no_access' : _publicAccessLevel;

    if (!galleryTenantId) {
      console.warn('Gallery doesnt contain a tenant ID, falling back to tenant access level', gallery);
      return this.returnHighest([tenantAccessLevel, publicAccessLevel]);
    }

    // Checking if you are part of the team
    const isPartOfTeam = this.state.tenants.find(t => t.id === galleryTenantId);

    // If this user is the creator of the gallery, they should always have edit access
    const owner = gallery.owner;
    if (this.state.user.id === ((owner as DisplayUser)?.id || (owner as string))) return 'edit';

    let GalleryGroupAccessLevel: GalleryAccessLevel = 'no_access';
    if (isPartOfTeam) {
      // Making sure that the galleries tenant is the active one so that we can rely on the activeTenantRole
      if (this.state.activeTenant?.id !== galleryTenantId) {
        await this.tenantService.setActiveTenant(isPartOfTeam);
      }

      // If the user is an admin or the owner, they always have edit access
      if (this.state.activeTenantRole === 'admin' || this.state.activeTenantRole === 'owner') {
        return 'edit';
      }

      GalleryGroupAccessLevel = await this.getMaxAccessLevelForGalleryUserGroupsUsers(gallery);
    }

    // Returning early if we already know we have edit access
    if ((tenantAccessLevel === 'edit' || GalleryGroupAccessLevel === 'edit') && isPartOfTeam) {
      return this.returnHighest([tenantAccessLevel, GalleryGroupAccessLevel]);
    }

    const sharedWithMeGallery =
      sharedWithMeGalleries.find(g => g.id === gallery.id) ||
      (gallery.parent_gallery_id && sharedWithMeGalleries.find(g => g.id === gallery.parent_gallery_id));
    if (sharedWithMeGallery) {
      return this.returnHighest([tenantAccessLevel, publicAccessLevel, sharedWithMeGallery.access_level]);
    }

    // Users who are not part of the team, and are not a gallery_member
    if (!isPartOfTeam) return publicAccessLevel;

    // Fallback for team members
    return this.returnHighest([tenantAccessLevel, publicAccessLevel, GalleryGroupAccessLevel]);
  }

  private getGalleryFromCache(galleryId: string) {
    return forkJoinSafe(
      this.observableLoader.getFromCacheFuzzy<GalleryDto[]>('/galleries?').map(resp => resp.observable.pipe(take(1)))
    ).pipe(
      map(galleryLists => galleryLists.flat().filter(Boolean)),
      map(galleries => galleries.find(g => g.id === galleryId)),
      switchMap(gallery => {
        if (gallery) return of(gallery);
        const galleryFromCacheDirect = this.observableLoader.getFromCacheFuzzy<GalleryDto>(
          '/galleries/' + galleryId
        )[0];
        if (galleryFromCacheDirect) return galleryFromCacheDirect.observable.pipe(take(1));
        return of(undefined);
      })
    );
  }

  private async getParentAccessLevels(
    gallery: GalleryDto
  ): Promise<[GalleryTenantAccessLevel, GalleryPublicAccessLevel]> {
    return this.loader.get('galleries_access-levels_' + gallery.parent_gallery_id, async () => {
      try {
        const parentGallery = await firstValueFrom(this.getGalleryFromCache(gallery.parent_gallery_id));
        if (parentGallery) return [parentGallery.tenant_access, parentGallery.public_access];

        const data: GalleryDto = await this.loader.get('galleries_with-parent-access_' + gallery.id, async () => {
          return lastValueFrom(
            this.http.get<GalleryDto>(
              `${this.config.apiEndpoint}/galleries/${
                gallery.id
              }?include[]=parent_public_access&include[]=parent_tenant_access${this.getPassword(gallery.id)}`,
              { context: new HttpContext().set(IGNORE_AUTH_ERRORS, true) }
            )
          );
        });
        return [data.parent_tenant_access, data.parent_public_access];
      } catch (err) {
        console.error(err);
        return [gallery.tenant_access || 'edit', gallery.public_access || 'view_only'];
      }
    });
  }

  public async getAccessLevels(gallery: GalleryDto): Promise<[GalleryTenantAccessLevel, GalleryPublicAccessLevel]> {
    if (!gallery.section) return [gallery.tenant_access, gallery.public_access];
    if (gallery.tenant_access && gallery.public_access) return [gallery.tenant_access, gallery.public_access];
    const [parentTenantAccessLevel, parentPublicAccessLevel] = await this.getParentAccessLevels(gallery);
    return [gallery.tenant_access ?? parentTenantAccessLevel, gallery.public_access ?? parentPublicAccessLevel];
  }

  private async getMaxAccessLevelForGalleryUserGroupsUsers(gallery: GalleryDto): Promise<GalleryAccessLevel> {
    if (!this.permissionService.canDisplayElement(billingScopes.groups.list)) return 'no_access';
    const galleryId = gallery.section ? gallery['parent_gallery_id'] : gallery.id;
    const groups = await this.userGroupService.getGalleryUserGroups(galleryId);
    const accessLevels = await Promise.all(
      groups.map(async group => {
        const users = await this.userGroupService.getUserGroupUsers(group.group);
        if (users.includes(this.state.user.id)) return group.access;
      })
    );
    return this.returnHighest(accessLevels);
  }

  public isGalleryGuest(
    gallery: GalleryDto,
    galleries: (GalleryDto & { access_level: GalleryAccessLevel })[]
  ): Observable<boolean> {
    if (!this.state.user) return of(false);
    const galleryTenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
    if (this.state.tenants?.find(t => t.id === galleryTenantId)) return of(false);
    return of(galleries.some(g => g.id === gallery.id));
  }

  private returnHighest(accessLevels: GalleryAccessLevel[]): GalleryAccessLevel {
    accessLevels.sort((a, b) => (accessLevelOrder.indexOf(a) < accessLevelOrder.indexOf(b) ? 1 : -1));
    return accessLevels[0] || 'no_access';
  }

  private getPassword(galleryId: string) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return '&password=' + encodeURIComponent(password);
  }
}
